/**
 * Add-on stock: display covers and engraved plaques at add-to-cart.
 * ---------------------------------------------------------------------------
 *
 * Covers and plaques sell with inventory policy CONTINUE (for the corporate
 * kits), so Shopify no longer refuses an add past their stock. This does,
 * for retail: an add that asks for more of one than is left - counting what the
 * cart already holds - is answered the way Shopify answers one past a DENY
 * variant's stock, a 422 with a description, without reaching the cart.
 *
 * Why at the network. Covers and plaques reach the cart from seven
 * configurators, two product pages and the quick adds; every one posts to
 * /cart/add, so this one wrapper holds the line for all of them (the same
 * reasoning as cf-sales-block.js). The configurators show the description
 * through `CFAddonStock.takeMessage()`; the product pages show a 422's
 * description on their own.
 *
 * The buttons are greyed or hidden by snippets/addon-in-stock before it comes
 * to this; this is what still stops a Set of six with four covers left, or a
 * second add of the last plaque. It fails open: if the check itself breaks, the
 * add goes through, as it would have before.
 *
 * Data: snippets/cf-addon-stock.liquid (stock per variant, names, messages).
 * Corporate kits add the kit variant, never a cover or plaque id, so they pass.
 */
(() => {
  const node = document.querySelector('script[type="application/json"][data-cf-addon-stock]');
  if (!node) return;

  let config;
  try {
    config = JSON.parse(node.textContent);
  } catch (e) {
    return;
  }
  const stock = (config && config.stock) || {};
  const names = (config && config.names) || {};
  const t = (config && config.t) || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const CART_ADD = /\/cart\/add(\.js|\.json)?\/?$/;
  const isCartAdd = (url) => {
    try {
      return CART_ADD.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  };

  const tracked = (id) => Object.prototype.hasOwnProperty.call(stock, id) && stock[id] !== null;

  /* The items of an add, from its JSON or form body: [{ id, quantity }]. */
  function itemsOf(body) {
    if (!body) return [];
    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (Array.isArray(data.items)) return data.items;
        if (data.id) return [{ id: data.id, quantity: data.quantity }];
      } catch (e) {
        const params = new URLSearchParams(body);
        if (params.get('id')) return [{ id: params.get('id'), quantity: params.get('quantity') }];
      }
      return [];
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return body.get('id') ? [{ id: body.get('id'), quantity: body.get('quantity') }] : [];
    }
    return [];
  }

  let lastMessage = '';

  /* What an add of `items` would leave short, as the message to show - or ''. */
  async function check(items, fetcher) {
    const want = {};
    (items || []).forEach((item) => {
      const id = String(item.id || item.variant_id || '');
      if (!tracked(id)) return;
      want[id] = (want[id] || 0) + (Math.max(1, parseInt(item.quantity, 10) || 1));
    });
    const ids = Object.keys(want);
    if (!ids.length) return '';

    const inCart = {};
    const res = await fetcher(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    (cart.items || []).forEach((line) => {
      const id = String(line.variant_id);
      if (want[id]) inCart[id] = (inCart[id] || 0) + line.quantity;
    });

    for (const id of ids) {
      const free = Math.max(0, stock[id] - (inCart[id] || 0));
      if (want[id] > free) {
        const name = names[id] || '';
        return free > 0
          ? String(t.left || '').replace('__N__', free).replace('__NAME__', name)
          : String(t.none || '').replace('__NAME__', name);
      }
    }
    return '';
  }

  const previous = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!isCartAdd(url) || !init || !init.body) return previous.apply(this, arguments);
    const self = this;
    const args = arguments;
    return (async () => {
      let message = '';
      try {
        message = await check(itemsOf(init.body), (u, o) => previous.call(window, u, o));
      } catch (e) {
        message = ''; // fail open: a broken check must not stop every add to cart
      }
      if (!message) return previous.apply(self, args);
      lastMessage = message;
      return new Response(JSON.stringify({ status: 422, message: 'Cart Error', description: message }), {
        status: 422,
        headers: { 'Content-Type': 'application/json' },
      });
    })();
  };

  window.CFAddonStock = {
    /* The message of the add this guard last refused, once; '' if none. */
    takeMessage() {
      const message = lastMessage;
      lastMessage = '';
      return message;
    },
  };
})();
